<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <heading :level="2">
        {{ $t('Dropdown response set') }}
      </heading>

      <icon
        class="cursor-pointer"
        icon="times"
        size="lg"
        @click.native="onClose"
      />
    </div>

    <div ref="responseContainer" class="overflow-y-auto" style="max-height: 300px">
      <div
        class="font-semibold mb-1"
        style="color: #363636"
        v-text="$t('template-builder.dropdown.add-responses')"
      ></div>

      <input-field
        v-for="(response, index) in temporaryResponseSet.responses" :key="response.uuid"
        :value="responseTitle(response)"
        :placeholder="responseTitlePlaceholder(response)"
        :icon-right="showDeleteResponseIcon ? 'trash-alt' : undefined"
        icon-right-clickable
        :errors="[]"
        @icon-right-click="onIconRightClick(response, index)"
        @input:enter="addResponse(index)"
        @input="value => onTitleChanged(response, value)"
      />

      <div class="mt-3 mb-5 flex items-center">
        <TranslationTooltip :active="isTranslationMode" :is-ai="false">
          <button
            type="button"
            class="btn btn-default btn-secondary mr-2"
            :disabled="isTranslationMode"
            @click="addResponse(temporaryResponseSet.responses.length - 1)"
            v-text="$t('Add item')"
          ></button>
        </TranslationTooltip>
        <span>{{ $t('or press enter') }}</span>
      </div>
    </div>

    <TranslationTooltip class="w-full" :active="isTranslationMode" :is-ai="false">
      <input-field
        v-model="temporaryResponseSet.title"
        name="title"
        :class="{ 'opacity-50': isTranslationMode }"
        :disabled="isTranslationMode"
        :label="$t('common.title')"
        :errors="[]"
      />
    </TranslationTooltip>
  </div>
</template>

<script>
  import DeleteButton from '@/_legacy/components/DeleteButton';
  import { Icon } from '@/ui/components/icon/Icon';
  import { InputField } from '@/ui/components/form/InputField';
  import { eventBus } from '@/_legacy/eventBus';
  import { t } from '@/ui/i18n/i18n';
  import { useManualTranslationsStore } from '@/stores/checklist/manualTranslations';
  import TranslationTooltip from '@/_legacy/views/ChecklistBuilder/TranslationTooltip.vue';

  export default {
    components: { TranslationTooltip, DeleteButton, Icon, InputField },
    props: {
      temporaryResponseSet: {
        type: Object,
        required: true,
      },
    },
    data() {
      const store = useManualTranslationsStore();
      const manualTitles = new Map();

      if (this.temporaryResponseSet.responses?.length > 0) {
        this.temporaryResponseSet.responses.forEach(
          response => manualTitles.set(response.uuid, store.getTitle(response.node_id, response.uuid)),
        );
      }

      return {
        manualTitles,
      };
    },
    computed: {
      showDeleteResponseIcon() {
        return this.temporaryResponseSet.responses.length > 1 && !this.isTranslationMode;
      },
      responseSetItemsInUse() {
        return this.$store.getters['checklist_builder/responseSetItemsInUse'];
      },
      isTranslationMode() {
        return this.$store.getters['checklist_builder/edit_response_set_modal'].isTranslationMode;
      },
    },
    methods: {
      onClose() {
        this.$emit('close');
      },
      onIconRightClick(response, index) {
        if (this.responseSetItemsInUse?.includes(response.uuid)) {
          eventBus.$emit('error', t('This response set option is in use, it can not be deleted!'));
        } else {
          this.$emit('deleteResponse', index);
        }
      },
      addResponse(index) {
        if (this.isTranslationMode) {
          return;
        }

        this.$emit('addResponse', index);

        this.$nextTick(() => {
          const responseContainer = this.$refs.responseContainer;
          responseContainer.scroll({ top: responseContainer.scrollHeight, behavior: 'smooth' });

          const inputs = Array.from(responseContainer.querySelectorAll('input'));
          const lastInput = inputs[inputs.length - 1];
          lastInput.focus();
        });
      },
      responseTitle(response) {
        return this.isTranslationMode
          ? (this.manualTitles.get(response.uuid) ?? response.title_translated)
          : response.title;
      },
      responseTitlePlaceholder(response) {
        return this.isTranslationMode && response.title ? response.title : t('Enter item here');
      },
      onTitleChanged(response, value) {
        if (this.isTranslationMode) {
          this.manualTitles.set(response.uuid, value);
          this.$emit('manualTitlesChanged', this.manualTitles);

          return;
        }

        this.$set(response, 'title', value);
      },
    }
  };
</script>
